import { AfterViewInit, Component, ComponentRef, inject, Injector, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { ValidatorType } from '@app/core/enums/validator-type.enum';
import { isAssetRelatedEntity } from '@app/core/model/entities/asset/assets-related-entity';
import {
  FIELD_ASYNC_PRECONDITIONS_INJECTION,
  FIELD_CONFIG_INJECTION,
  FIELD_ENTITY_INJECTION,
  FIELD_EXTRA_DATA,
  FIELD_PERMISSIONS_INJECTION,
  FIELD_PRECONDITIONS_INJECTION
} from '@app/core/model/other/field-config';
import {
  AbstractFieldBuilder,
  AbstractFieldGroupBuilder,
  FieldStateMode
} from '@app/shared/components/fields/abstract.field';
import { FieldAnchorDirective } from '@app/shared/components/fields/field-anchor.directive';
import { getFieldBuilder } from '@app/shared/components/fields/field-builder-selector';
import { BehaviorSubject, ReplaySubject } from 'rxjs';

@Component({
  selector: 'group-field-builder',
  templateUrl: './group-field-builder.component.html',
  styleUrls: ['./group-field-builder.component.scss'],
  standalone: false
})
export class GroupFieldBuilderComponent extends AbstractFieldGroupBuilder implements OnInit, AfterViewInit {
  public Permission = PermissionEnum;

  private injector = inject(Injector);

  @ViewChild(FieldAnchorDirective, {static: true}) public fieldHost: FieldAnchorDirective;
  // Hold component references to all builders inside the group
  public fieldBuilders: ComponentRef<AbstractFieldBuilder<any>>[] = [];

  public ngOnInit(): void {
    this.setFieldGroupInfo([], {
      state: new ReplaySubject<FieldStateMode>(1),
      isSingleField: false,
      value: {},
      isDisplayed: new BehaviorSubject<boolean>(false),
      initialValue: {},
      requiredTrue: {}
    });
  }

  public ngAfterViewInit(): void {
    this.setupHooks();

    // Subscribe to any change in state of the group
    this.getFieldGroupState().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((newMode) => {

      // The behavior of the creation form should be the same as when editing an existing one.
      // When creating an entity we force the form to use the EDIT mode.
      // Allowing users to interact with the form and write data in its fields.
      if (this.isCreatingEntity) {
        newMode = FieldStateMode.EDIT;
      }

      if (newMode === FieldStateMode.AFTER_SAVE) {
        this.refreshEntity();
        this.setFieldGroupInitialValue(JSON.parse(JSON.stringify(this.getFieldGroupValue())));
        this.getNextState();
      } else {
        if (newMode === FieldStateMode.EDIT) {
          // find which fieldBuilder requested focus
          const fieldBuilder = this.fieldBuilders.find(fb => fb.instance.hasRequestedFocus());
          if (fieldBuilder) { // if found, set focus
            fieldBuilder.instance.focus();
            fieldBuilder.instance.resetRequestFocus();
          } else { // if no field requested focus, set the first one focused
            this.fieldBuilders[0]?.instance.focus();
          }
        }
        this.currentMode = newMode;
      }
    });

    this.initFieldGroup();
  }

  public initFieldGroup(): void {
    const fieldGroupContainerRef = this.fieldHost.viewContainerRef;
    fieldGroupContainerRef.clear();

    // Cannot edit this field's group if the user has no write access to some fields in this group (except for creation popup)
    if (!this.isCreatingEntity) {
      this.preconditionsForEdition &&= this.fieldGroup.children.every(fieldConfig =>
        this.accessManager.hasFieldWriteAccess(fieldConfig.code, fieldConfig.fieldEntityType)
      );
    }

    this.fieldGroup.children.filter(fieldConfig => {
      // Remove fields not related to the current asset type
      const fieldAssetTypesIds: string[] = fieldConfig.field.validators
        .firstOrNull(v => v.type === ValidatorType.FOR_ASSET_TYPES)
        ?.definition?.split(',');

      const validAssetTypes = !fieldAssetTypesIds || (
        isAssetRelatedEntity(this.entity)
        && this.entity.relatedAssets.some(({typeId}) => fieldAssetTypesIds.includes(typeId))
      );

      // For creation pop-up, remove fields with no write access (WRITE_ON_CREATE fields are writable here)
      const hasFieldWriteAccess = !this.isCreatingEntity || this.accessManager.hasFieldWriteOnCreateAccess(
        fieldConfig.code,
        fieldConfig.fieldEntityType
      );

      return hasFieldWriteAccess && validAssetTypes;
    })
      .forEach((fieldConfig, index) => {
        // get the field type to create
        const fieldComponent = getFieldBuilder(fieldConfig);

        if (!fieldConfig.field.computed) {
          // If at least one member of the group is not computed, the group must be displayed
          this.getFieldGroupInfo(['isDisplayed']).next(true);
        }
        // Create the corresponding component if it is recognized
        if (fieldComponent.fieldBuilder !== null) {
          const fieldRef = fieldGroupContainerRef.createComponent(fieldComponent.fieldBuilder, {
            index: index,
            injector: Injector.create({
              parent: this.injector,
              providers: [
                {provide: FIELD_CONFIG_INJECTION, useValue: fieldConfig},
                {provide: FIELD_ENTITY_INJECTION, useValue: this.entity},
                {
                  provide: FIELD_PRECONDITIONS_INJECTION,
                  useValue: this.preconditionsForEdition && this.validationService.isFieldEditable(
                    fieldConfig,
                    this.entity
                  )
                },
                {
                  provide: FIELD_ASYNC_PRECONDITIONS_INJECTION,
                  useValue: this.validationService.validateAsyncConditions(this.entity, fieldConfig.conditionsToEdit)
                },
                {provide: FIELD_PERMISSIONS_INJECTION, useValue: this.permissionsForEdition},
                {
                  provide: FIELD_EXTRA_DATA, useValue: {
                    isSingleField: false,
                    fieldGroupCode: this.fieldGroup.code,
                    propertiesPath: this.data.propertiesPath,
                    focusable: fieldComponent.focusable,
                    formId: this.data.formId
                  }
                }
              ]
            })
          });
          fieldRef.changeDetectorRef.detectChanges();

          this.fieldBuilders.push(fieldRef);
        } else {
          console.warn('Le type de champ "' + fieldConfig + '" n\'est pas reconnu');
        }

      });

    this.getNextState();
  }

  public cancel(): void {
    this.fieldBuilders.forEach(fieldBuilder => {
      fieldBuilder.instance.cancel();
    });
    super.cancel();
  }

  public isGroupInvalid(): boolean {
    return this.fieldBuilders.some(fieldBuilder => {
      return fieldBuilder.instance.form.invalid || fieldBuilder.instance.form.pending;
    });
  }
}
